<template>
  <div
      class="input_wrapper"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
  >
    <input
        v-if="!disabled"
        :id="`payment_${timesheetId}_${rowIndex}`"
        v-model="inputValue"
        :class="['payday_input', paintedClass, { 'input-error': errors }]"
        :name="`payment_${timesheetId}_${rowIndex}`"
        inputmode="decimal"
        pattern="^(\d+(\.\d+)?|[HSMhsm])$"
        @blur="handleBlur"
        @click="handleClick"
        @focus="isTyping = true"
        @input="handleInput"
        @keydown="handleKeyDown"
    />

    <div
        v-else
        :class="['payday_input', paintedClass, { 'input-error': errors }]"
        @click="handleClick"
    >
      {{ inputValue }}
    </div>

    <div v-if="hovered && errors && errors.length > 0" class="tooltip">
      <ul v-if="errors">
        <li v-for="(error, i) in errors" :key="i">{{ error }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'payday_form_horizontal',
  props: {
    errors: { required: false },
    timesheetId: { required: true },
    rowIndex: { required: true },
    value: { type: Object, required: true }, // { amount, absence_type, pay_date }
    onClick: { type: Function, default: () => {} },
    disabled: Boolean
  },
  data() {
    return {
      hovered: false,
      isTyping: false,
      inputValue:
          this.value.absence_type ||
          (this.value.amount > 0 ? this.value.amount.toString() : '')
    };
  },
  computed: {
    paintedClass() {
      if (this.value.absence_type) return this.value.absence_type;
      const n = parseFloat(this.value.amount);
      return n > 0 ? 'worked' : '';
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (this.isTyping) return;
        const derived =
            newVal.absence_type ||
            (newVal.amount > 0 ? String(newVal.amount) : '');
        if (derived !== this.inputValue) this.inputValue = derived;
      }
    }
  },
  methods: {
    handleKeyDown(event) {
      let direction = null;
      switch (event.key) {
        case 'Tab': direction = event.shiftKey ? -1 : 1; break;
        case 'Enter':
        case 'ArrowRight': direction = 1; break;
        case 'ArrowLeft': direction = -1; break;
        default: return;
      }

      event.preventDefault(); // we manage nav
      this.handleBlur(); // commit before moving

      const moved = this.moveWithinRow(direction);
      if (!moved) {
        this.$emit('advanceFocus', {
          timesheetId: this.timesheetId,
          rowIndex: this.rowIndex,
          direction,
          edge: direction === -1 ? 'left' : 'right'
        });
      }
    },

    moveWithinRow(direction) {
      const rowEl = this.$el.closest('tr');
      if (!rowEl) return false;

      const inputs = Array.from(
          rowEl.querySelectorAll('.day_col input:not([disabled])')
      );
      if (!inputs.length) return false;

      const active = document.activeElement;
      let idx = inputs.indexOf(active);

      if (idx === -1) {
        const myId = `payment_${this.timesheetId}_${this.rowIndex}`;
        const mine = rowEl.querySelector(`#${(window.CSS && CSS.escape) ? CSS.escape(myId) : myId}`);
        idx = inputs.indexOf(mine);
        if (idx === -1) return false;
      }

      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= inputs.length) return false;

      const target = inputs[nextIdx];
      this.$nextTick(() => {
        target?.focus?.();
        try { target?.select?.(); } catch (_) {}
      });
      return true;
    },

    handleInput() {
      this.isTyping = true;
      const raw = (this.inputValue || '').toUpperCase();
      const decimalPattern = /^(\d+(\.\d*)?|\.\d*|\.)?$/;
      let next = { ...this.value };

      if (decimalPattern.test(raw)) {
        const n = parseFloat(raw);
        if (raw === '' || isNaN(n)) {
          next.amount = 0;
          next.absence_type = null;
        } else {
          if (n < 0) {
            this.revertToProp();
            return;
          }
          next.amount = n;
          next.absence_type = null;
        }
        this.$emit('input', next);
        this.inputValue = raw;
      } else if (/^[HSM]$/.test(raw)) {
        next.amount = 0;
        next.absence_type = raw;
        this.$emit('input', next);
        this.inputValue = raw;
      } else {
        this.revertToProp();
      }
    },

    handleBlur() {
      let s = (this.inputValue || '').trim();

      if (/^[HSM]$/.test(s.toUpperCase())) {
        this.isTyping = false;
        return;
      }

      if (s === '' || s === '.') {
        this.inputValue = '';
        this.$emit('input', { ...this.value, amount: 0, absence_type: null });
        this.isTyping = false;
        return;
      }

      if (s.startsWith('.')) s = '0' + s;
      if (s.endsWith('.')) s = s + '0';

      const n = parseFloat(s);
      if (!isNaN(n) && n >= 0) {
        this.inputValue = String(n);
        this.$emit('input', { ...this.value, amount: n, absence_type: null });
      } else {
        this.revertToProp();
      }
      this.isTyping = false;
    },

    handleClick(event) {
      this.onClick({
        timesheetId: this.timesheetId,
        rowIndex: this.rowIndex,
        value: this.value,
        event
      });
    },

    revertToProp() {
      this.inputValue =
          this.value.absence_type ||
          (this.value.amount > 0 ? String(this.value.amount) : '');
    }
  }
};
</script>

<style scoped>
.payday_input {
  background-color: white;
  color: black;
  width: 100%;
  min-width: 58px;
  min-height: 21px;
  height: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-sizing: border-box;
  text-align: center;
}

.worked { background-color: lightgreen; }
.H { background-color: yellow; }
.S { background-color: lightpink; }
.M { background-color: #EAEAEA; }

.input_wrapper { position: relative; width: 100%; height: 100%; text-align: center; }

.tooltip {
  position: absolute; top: 100%; left: 0;
  background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb;
  padding: 8px; font-size: 12px; border-radius: 4px; white-space: pre-wrap;
  z-index: 10; margin-top: 4px; max-width: 300px; width: 200px; height: 100px;
  overflow: scroll; box-shadow: 0 2px 8px rgba(0,0,0,.15); opacity: 1;
}
.tooltip ul { margin: 0; padding-left: 18px; }
.tooltip li { list-style-type: disc; }
</style>
